import { capitalize } from '@/utils/capitalize';
import { BillingPeriodSummary } from './BillingPeriodSummary';
import { CompanyContact } from './CompanyContact';
import { Cost } from './Cost';
import { Item } from './Item';
import { Person } from './Person';
import { PhysicalAddress } from './PhysicalAddress';
import { PublisherFee } from './PublisherFee';
import { Timezone } from './Timezone';

export enum DistFeeType {
  PERCENT = 'PERCENT',
  FLAT_FEE = 'FLAT_FEE',
}

export enum CompanyContactPreferences {
  NO_CONTACT = 'NO_CONTACT',
  EMAIL = 'EMAIL',
  PHONE = 'PHONE',
  EMAIL_AND_PHONE = 'EMAIL_AND_PHONE',
}

export enum PriceTransform {
  FIXED_PRICE = 'FIXED_PRICE',
  PERCENT = 'PERCENT',
}

export enum SchoolIntegrationType {
  COURSE_CODES = 'COURSE_CODES',
  CANVAS = 'CANVAS',
  D2L = 'D2L',
  BLACKBOARD = 'BLACKBOARD',

  VITALSOURCE = 'VITALSOURCE',
  MOODLE = 'MOODLE',
}

export enum CompanyType {
  PUBLISHER = 'PUBLISHER',
  SCHOOL = 'SCHOOL',
  DISTRIBUTOR = 'DISTRIBUTOR',
  INTERNAL = 'INTERNAL',
}

export enum DefaultOpt {
  IN = 'IN',
  OUT = 'OUT',
}

export enum DLPSource {
  ACCESS = 'ACCESS',
  RETAIL = 'RETAIL',
}
export enum AccessType {
  ACCESS_PROGRAM = 'ACCESS_PROGRAM',
  ACCESS_CODES = 'ACCESS_CODES',
}

export enum AccountingType {
  CUSTOMER = 'CUSTOMER',
  VENDOR = 'VENDOR',
}

export enum RetailChannels {
  ECOMMERCE = 'ECOMMERCE',
  ACCESS_CODES = 'ACCESS_CODES',
  ALL = 'ALL',
}

export const displayCompanyType = (companyType: CompanyType) => {
  return companyType ? capitalize(companyType.toLowerCase()) : '-';
};

export interface Company extends Item {
  name: string;
  domain?: string;
  type: CompanyType;
  imageUrl?: string;
  billingPeriodSummaries?: BillingPeriodSummary[];
  accessType?: AccessType;
  timezone?: Timezone;
  accountingType?: AccountingType;
  accountingCustomerId?: string;
  accountingVendorId?: string;
  isAccountingSynced?: boolean;
  email?: string;
  autoEnroll?: boolean;
  phone?: string;

  contactPrefs?: CompanyContactPreferences;
  contactFirstName?: string;
  contactLastName?: string;
  billingAddressId?: number;
  billingAddress?: PhysicalAddress;
  shippingAddressId?: number;
  shippingAddress?: PhysicalAddress;
  publisherRetailPercent?: number;
  publisherRetailPercentCharge?: number;
  fullglassRetailPercent?: number;
  billingCompanyId?: number;
  billingCompany?: Company;
  defaultOpt?: DefaultOpt;
  onixId?: string;

  accountManagerId?: number;
  accountManager?: Person | null;

  distributorId?: number;
  distributor?: Company | null;
  percentOnboard?: number;

  integrationType?: SchoolIntegrationType;

  lmsAdminContactId?: number;
  lmsAdminContact?: CompanyContact;

  bookstoreManagerContactId?: number;
  bookstoreManagerContact?: CompanyContact;

  courseMaterialsManagerContactId?: number;
  courseMaterialsManagerContact?: CompanyContact;

  accessManagerContactId?: number;
  accessManagerContact?: CompanyContact;

  otherContactId?: number;

  defaultCostId?: number;
  defaultCost?: Cost;

  otherContact?: CompanyContact;
  distributorContactId?: number;
  distributorContact?: CompanyContact;

  retailChannels?: RetailChannels;
  ipedsId?: string;
  distNetFeeType?: DistFeeType;
  distNetFeeAmount?: number;

  dlpType?: DistFeeType;
  dlpAmount?: number;

  dlpSource?: DLPSource;
  includeImprints?: boolean;
  defaultPublisherFeeId?: number;
  defaultPublisherFee?: PublisherFee;

  retailPriceValue?: number;
  accessPriceValue?: number;

  accessPriceTransform?: PriceTransform;

  retailPriceTransform?: PriceTransform;

  notes?: string;

  lockdownUrl?: string;
}

export const isPublisherCompany = (company: Company) => {
  return company?.type === CompanyType.PUBLISHER;
};

export const isSchoolCompany = (company: Company) => {
  return company?.type === CompanyType.SCHOOL;
};

export const isInternalCompany = (company: Company) => {
  return company?.type === CompanyType.INTERNAL;
};

export interface CompanyFilterList {
  search?: string;
  type?: CompanyType;
  isActive?: boolean;
  accountManagerId?: number | 'all'; // single-manager filter (develop)
  accountManagerIds?: string; // multi-manager filter: IDs joined by '-' (issues/1206)
  includeImprints?: boolean;
}

export const displayAccessType = (accessType?: AccessType) => {
  switch (accessType) {
    case AccessType.ACCESS_PROGRAM:
      return 'Access Program';
    case AccessType.ACCESS_CODES:
      return 'No Access Program';
    default:
      return '-';
  }
};

export const displayRetailChannels = (retailChannels: RetailChannels) => {
  switch (retailChannels) {
    case RetailChannels.ECOMMERCE:
      return 'E-commerce Only';
    case RetailChannels.ACCESS_CODES:
      return 'Access Codes Only';
    case RetailChannels.ALL:
      return 'E-commerce & Access Codes';
    default:
      return '-';
  }
};

export const displayDefaultOpt = (defaultOpt: DefaultOpt) => {
  switch (defaultOpt) {
    case DefaultOpt.IN:
      return 'In';
    case DefaultOpt.OUT:
      return 'Out';
    default:
      return '-';
  }
};

export const calculateAccessPrice = (
  transform: PriceTransform,
  value: number,
  priceAmount: number
): number => {
  if (transform === PriceTransform.FIXED_PRICE) {
    return value && !isNaN(value) ? value : priceAmount;
  } else if (transform === PriceTransform.PERCENT) {
    return priceAmount * (Number(value ?? 100) / 100);
  } else {
    return priceAmount;
  }
};
export const calculateRetailPrice = (
  transform: PriceTransform,
  value: number,
  priceAmount: number
): number => {
  if (transform === PriceTransform.FIXED_PRICE) {
    return value ?? priceAmount;
  } else if (transform === PriceTransform.PERCENT) {
    return priceAmount * (Number(value ?? 100) / 100);
  } else {
    return priceAmount;
  }
};

export const displayDlpSource = (dlpSource?: DLPSource) => {
  switch (dlpSource) {
    case DLPSource.ACCESS:
      return 'Access Net Price';
    case DLPSource.RETAIL:
      return 'Retail Net Price';
    default:
      return '-';
  }
};

export const displayIntegrationType = (integrationType?: SchoolIntegrationType) => {
  switch (integrationType) {
    case SchoolIntegrationType.CANVAS:
      return 'Canvas';
    case SchoolIntegrationType.D2L:
      return 'D2L';
    case SchoolIntegrationType.BLACKBOARD:
      return 'Blackboard';
    case SchoolIntegrationType.COURSE_CODES:
      return 'Course Codes';
    case SchoolIntegrationType.MOODLE:
      return 'Moodle';
    case SchoolIntegrationType.VITALSOURCE:
      return 'VitalSource';
    default:
      return '-';
  }
};

export const displayDistNetFeeAmount = (company: Company) => {
  if (
    company?.distNetFeeType &&
    company?.distNetFeeAmount !== null &&
    company?.distNetFeeAmount !== undefined &&
    !isNaN(company?.distNetFeeAmount)
  ) {
    const amount = company?.distNetFeeAmount ?? 0;
    if (amount === undefined || amount === null || isNaN(amount)) {
      return 'N/A';
    }
    const type = company.distNetFeeType === DistFeeType.FLAT_FEE ? '$' : '%';

    if (type === '$') {
      return `$${Number(amount).toFixed(2)}`;
    } else {
      return `${Number(amount).toFixed(2)}%`;
    }
  } else {
    return 'N/A';
  }
};
export const displayDLP = (company: Company) => {
  if (
    company?.dlpType &&
    company?.dlpAmount !== null &&
    company?.dlpAmount !== undefined &&
    !isNaN(company?.dlpAmount)
  ) {
    const amount = company?.dlpAmount ?? 0;
    if (amount === undefined || amount === null || isNaN(amount)) {
      return 'N/A';
    }
    const type = company.dlpType === DistFeeType.FLAT_FEE ? '$' : '%';
    if (type === '$') {
      return `$${Number(amount).toFixed(2)}`;
    } else {
      return `${Number(amount).toFixed(2)}%`;
    }
  } else {
    return 'N/A';
  }
};
